import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, iif } from 'rxjs';
import { catchError, retryWhen, switchMap, timeout } from 'rxjs/operators';
import { AppConfigService } from '../config/app-config.service';
import { HttpRequestMethods } from '../models/api.constants';
import {
  HttpErrorHandler,
  HttpHeadersPayload
} from '../models/common.interfaces';
import { ApiHelpers } from './api-helpers';

/** A builder class to create http requests based on different parameters, following the Builder Design Pattern. */
export class ApiBuilder<RequestType, ResponseType> extends ApiHelpers {
  private _appName!: string;
  private _method!: HttpRequestMethods;
  private _payload!: RequestType;
  private _endpointKey!: string;
  private _errorHandler: HttpErrorHandler;
  private _headersPayload!: HttpHeadersPayload;
  private _timeOut!: number;

  constructor(
    private httpClient: HttpClient,
    private appConfig: AppConfigService
  ) {
    super();
    // Builder default initializers
    this._errorHandler = () => null as never;
  }

  /** Set the application name such as CANCEL_REFUND, BAGGAGE_CALCULATOR, etc. */
  app(appName: string) {
    this._appName = appName;
    return this;
  }

  /** Set http request method (POST, GET, etc..) */
  method(method: HttpRequestMethods): ApiBuilder<RequestType, ResponseType> {
    this._method = method;
    return this;
  }

  /** Set endpoint url key from idp-config.json object */
  endpointKey(endpointKey: string): ApiBuilder<RequestType, ResponseType> {
    this._endpointKey = endpointKey;
    return this;
  }

  /** Set http request payload */
  payload(requestObject: RequestType): ApiBuilder<RequestType, ResponseType> {
    this._payload = requestObject;
    return this;
  }

  /** Set the Http request headers */
  httpHeaders(payload: HttpHeadersPayload) {
    this._headersPayload = payload;
    return this;
  }

  /**  Set the error handler method */
  errorHandler(handler: HttpErrorHandler) {
    this._errorHandler = handler;
    return this;
  }

  /** Set timeout value */
  timeOut(timeOut: number) {
    this._timeOut = timeOut;
    return this;
  }

  /** The builder function that build the http request and returns it */
  create(): Observable<ResponseType> {
    return this.appConfig.getConfigValue(this._appName, this._endpointKey).pipe(
      switchMap((endpointUrl) => {
        if (this._override) {
          endpointUrl = this._overrideEndpoint(endpointUrl);
        }
        const result = this._httpRequestMapper<RequestType, ResponseType>(
          endpointUrl,
          this._method,
          this._payload,
          this._headersPayload,
          this._queryParams
        ).pipe(catchError((error) => this._catchError(error)));
        return iif(
          () => Boolean(this._timeOut),
          result.pipe(
            timeout(this._timeOut),
            retryWhen((error) => {
              return this._retryWhen(error);
            }),
            catchError((error) => this._catchError(error))
          ),
          result
        );
      })
    );
  }

  private _catchError(error: HttpErrorResponse): Observable<never> {
    return this._errorHandler(
      error,
      this._appName,
      this._library,
      this._endpointKey
    );
  }

  /** A mapper function that maps and creates a http request based on the `method` */
  private _httpRequestMapper<Request, Response>(
    endpointUrl: string,
    method: HttpRequestMethods,
    requestObject?: Request,
    payload?: HttpHeadersPayload,
    queryParams = ''
  ): Observable<Response> {
    const url = endpointUrl + queryParams;
    switch (method) {
      case HttpRequestMethods.POST:
        return this.httpClient.post<Response>(url, requestObject, {
          headers: this._createHttpHeaders(payload),
          withCredentials: this._withCredentials
        });
      case HttpRequestMethods.GET:
        return this.httpClient.get<Response>(url, {
          withCredentials: this._withCredentials
        });
      case HttpRequestMethods.GET_HEADERS:
        return this.httpClient.get<Response>(url, {
          headers: this._createHttpHeaders(payload),
          withCredentials: this._withCredentials
        });
      default:
        return this._httpFullResponseMapper<Request, Response>(
          url,
          method,
          this.httpClient,
          payload,
          requestObject
        );
    }
  }
}
